import { getTestAttributes } from '@chillibean/component-library';
import { useState, useEffect } from 'react';

import { regexParser, charTypes } from '../../utils/utils';

export function InputFormatter(props) {
  const { fieldID, character_limit, character_restriction, input_format, handleChange, readOnly } = props;
  const isRealNumber = typeof character_limit === 'number' && Number.isNaN(character_limit) === false;
  const selectedCharacterRestriction = character_restriction && charTypes[character_restriction].regex;

  const [selectedInputBox, setSelectedInputBox] = useState(null);
  const [inputFormatState, setInputFormatState] = useState(null);
  const [firstRender, setFirstRender] = useState(true);

  useEffect(() => {
    if (character_restriction) {
      manageInputFormatterState(character_limit, input_format);
    }
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 1);
  }, [character_limit, input_format]);

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      return;
    }
    if (isRealNumber && character_restriction) {
      const toSetState = new Array(character_limit).fill(selectedCharacterRestriction);
      setInputFormatState(toSetState);
      setSelectedInputBox(0);
      handleChange(toSetState.join(''), 'input_format');
    }
  }, [character_restriction]);

  const manageInputFormatterState = (characterLimit, inputFormat) => {
    if (isRealNumber && typeof inputFormat !== 'string') {
      const toSetState = new Array(characterLimit).fill(selectedCharacterRestriction);
      setInputFormatState(toSetState);
      setSelectedInputBox(0);
      handleChange(toSetState.join(''), 'input_format');
    } else if (isRealNumber && typeof inputFormat === 'string') {
      const valFromRedux = regexParser(input_format, characterLimit);
      const toSetState = valFromRedux.map((charType) => {
        if (character_restriction !== 'letters_and_numbers') {
          return charType === '-' ? charType : selectedCharacterRestriction;
        }
        return charType.length > 0 ? charType : selectedCharacterRestriction;
      });
      setInputFormatState(toSetState);
      handleChange(toSetState.join(''), 'input_format');
    } else if (characterLimit === null && typeof inputFormat === 'string') {
      handleChange(null, 'input_format');
    }
  };

  const onClickSetSelectedInputBox = (identifier, event) => {
    if (identifier === 0) {
      setSelectedInputBox(identifier);
    } else {
      const siblings = event.target.parentElement.children;
      const prevSiblingText = siblings[identifier - 1].innerText || siblings[identifier - 1].textContent;
      if (prevSiblingText.length > 0) {
        setSelectedInputBox(identifier);
      }
    }
  };

  const onClickSetInputFormatState = (event) => {
    event.preventDefault();
    if (typeof selectedInputBox === 'number' && inputFormatState) {
      const keyFromSymbol = getKeyFrom(event.target.value, 'text');
      const updatedState = [...inputFormatState];
      updatedState[selectedInputBox] = charTypes[keyFromSymbol].regex;
      setInputFormatState(updatedState);
      const valToUpdateRedux = updatedState.join('');
      handleChange(valToUpdateRedux, 'input_format');
    }
  };

  const getKeyFrom = (givenValue, givenKey) => {
    let keyToReturn = null;
    for (const key in charTypes) {
      if (charTypes[key][givenKey] === givenValue) {
        keyToReturn = key;
      }
    }
    return keyToReturn;
  };

  const inputBoxGenerator = () => {
    if (inputFormatState) {
      const createdBoxes = inputFormatState.map((inputFormat, index) => {
        const retrievedKey = inputFormat.length > 0 ? getKeyFrom(inputFormat, 'regex') : inputFormat;
        const symbolToDispay = retrievedKey.length > 0 ? charTypes[retrievedKey].symbol : retrievedKey;
        const currentlySelectedClass =
          index === selectedInputBox ? ' inputFormatter__inputBox-presenter--currentlySelected' : '';
        return (
          <div
            className={`inputFormatter__inputBox-presenter${currentlySelectedClass}`}
            onClick={(event) => onClickSetSelectedInputBox(index, event)}
            key={`${fieldID}${index}${symbolToDispay}`}
            {...getTestAttributes(`${fieldID}-box-${index + 1}`)}
          >
            {symbolToDispay}
          </div>
        );
      });
      return createdBoxes;
    }
  };

  const filterButtonsByCharacterRestriction = () => {
    const charTypeKeys = Object.keys(charTypes);
    if (character_restriction !== 'letters_and_numbers') {
      return charTypeKeys
        .filter((charTypeKey) => charTypeKey === character_restriction || charTypeKey === 'hyphen')
        .map((charTypeKey) => charTypes[charTypeKey]);
    }
    return Object.values(charTypes);
  };

  const generateCharTypeButtons = () => {
    const charTypeButtons = filterButtonsByCharacterRestriction();
    let retrievedKey = null;
    if (typeof selectedInputBox === 'number') {
      retrievedKey = getKeyFrom(inputFormatState[selectedInputBox], 'regex');
    }

    return charTypeButtons.map(({ text, symbol }, index) => {
      const currentlySelectedClass =
        retrievedKey && charTypes[retrievedKey].text === text ? ' inputFormatter__selector--currentlySelected' : '';
      return (
        <button
          className={`inputFormatter__selector${currentlySelectedClass}`}
          key={`${fieldID}${index}${text}`}
          onClick={onClickSetInputFormatState}
          value={text}
          disabled={readOnly}
          {...getTestAttributes(`${fieldID}-type-[${symbol}]`)}
        >
          {text.toUpperCase()}
        </button>
      );
    });
  };

  return character_restriction && character_limit && Number.isNaN(character_limit) === false ? (
    <div className="inputFormatter__wrapper">
      <div className="inputFormatter__group">
        <div className="inputFormatter" {...getTestAttributes(`${fieldID}-form-field`)}>
          Please click on each box to select a character type
          <div className="inputFormatter__inputBox-container">{inputBoxGenerator()}</div>
          <div className="inputFormatter__line-container">
            <hr />
          </div>
          <div className="inputFormatter__characterTypes-container">{generateCharTypeButtons()}</div>
        </div>
      </div>
    </div>
  ) : null;
}

export default InputFormatter;
